import {gql, useQuery} from '@apollo/client';
import {ErrorDisplay, LoadingSpinner} from 'common';
import {useSearchParam} from 'common/hook';
import {
    portalReferralParse,
    Referral,
    ReferralOffer,
    ReferralOfferToOffer
} from 'placement/domains';
import {useState} from 'react';
import {useParams} from 'react-router';
import {ProviderOfferDetailLeftRail} from './ProviderOfferDetailLeftRail';
import {ProviderOfferDetailRightRail} from './ProviderOfferDetailRightRail';
import {ProviderOfferNavigationBar} from './ProviderOfferNavigationBar';
import {groupBy, find} from "lodash";
import {BehaviorSubject} from 'rxjs';
import { getPlacementPageFromSessionStorage } from 'placement/core/persistence/SessionStoredPlacementPage';

export const REFERRAL_OFFER_QUERY = gql`
    query offersByReferral($referralGuids: [String]!, $referralGuidStrings: [String]!) {
            referrals(guids: $referralGuids) {
                id
                guid
                caseNumber
                serviceTypes
                serviceLocations
                serviceLocationsParaprofessional
                serviceLocationsMidLevel
                serviceLocationsHighLevel
                serviceLocationsGroupCare
                serviceNotes
                referralConsiderations
                status
                placementType
                location
                clientAge
                requiredLanguage
                clientName
                directCareHours
                midLevelHours
                highLevelHours
                groupCareHours
                scheduleNotes
                daysInPlacement
                salesforceCaseUrl
                authDirectCareHours
                authDirectCareBeneficialHours
                authMidLevelHours
                authMidLevelBeneficialHours
                authHighLevelHours
                authHighLevelBeneficialHours
                authGroupCareHours
                authGroupCareBeneficialHours
                careMediatedMidHours
                careMediatedMidMinHours
                careMediatedHighHours
                careMediatedHighMinHours
                practDirectBeneficialHours
                practMidBeneficialHours
                practHighBeneficialHours
                practGroupBeneficialHours
                practDirectOptimalHours
                practMidOptimalHours
                practHighOptimalHours
                practGroupOptimalHours
                authTherapyTreatmentUnitsPer
                authTherapyTreatmentUnitType
                authTherapyTreatmentUnit
                longitude
                latitude
                matchedOffer {
                    guid
                }
                clientProviderPreferences {
                    sfProviderId
                    clientPreference
                    preferenceReason
                }
            }
            offersByReferrals(referralGuids: $referralGuidStrings) {
                id
                guid
                conversationSummary {
                    urgent
                    hasConversation
                    providerUnreadCount
                    clientPlacementTeamUnreadCount
                }
                status
                firstServiceDate
                lastServiceDate
                comments
                directCareHours
                midLevelSupervisionHours
                highLevelSupervisionHours
                groupCareHours
                provideAllEstimatedHours
                offerHourPercentage
                submittedAt
                provideServicesInClinic
                provideServiceInRequiredLanguage
                psychotherapyOffer
                referralGuid
                declinedReason
                draftStaffingPlan {
                    comments
                    fullOfferStartDate
                    reviewedAndApprovedByClientFamilyDate
                    offeredParaprofessionalHours
                    offeredMidLevelHours
                    offeredHighLevelHours
                    offeredGroupCareHours
                }
                submittedBy {
                  id
                  guid
                  firstName
                  lastName
                }
                clinic {
                    location
                    street
                    city
                    stateCode
                    zipCode
                    longitude
                    latitude
                }
                otherClinicLocation
                provider {
                    guid
                    sfId
                    name
                    numOfAcceptedOffers
                }
                distanceToClinic {
                    distanceInMiles
                    timeInMillis
                }
                treatmentOffered
                caregiverMediatedMidLevelHours
                caregiverMediatedHighLevelHours
                startDateCaregiverMediatedTreatment
                startDatePractMediatedTreatment
                serviceLocationType
                serviceLocationParaprofessionalType
                serviceLocationMidLevelType
                serviceLocationHighLevelType
                serviceLocationGroupCareType
            }
    }
`

type OfferByReferralResponse = {
    offersByReferrals: Array<ReferralOffer>;
    referrals: Array<Referral>;
}

type Params = {
    id: string;
}

export function ProviderOfferDetailPage() {
    let {id} = useParams<Params>();
    const siblingReferralGuids = useSearchParam().getAll('siblingReferralGuids');
    const recentPage = getPlacementPageFromSessionStorage();

    const [selectedReferral$] = useState(new BehaviorSubject<Referral | null>(null));

    const {loading, data, error, refetch} = useQuery<OfferByReferralResponse, {}>(
        REFERRAL_OFFER_QUERY, {
            variables: {
                referralGuids: [id, ...siblingReferralGuids],
                referralGuidStrings: [id, ...siblingReferralGuids]
            },
            fetchPolicy: 'no-cache',
            onError(e) {
                console.error(e);
            }
        }
    );

    if (loading) {
        return (
            <LoadingSpinner spacing={20} centerScreen/>
        );
    }

    const errMsg = error ? 'Some error happened' : (data!.referrals!.length < 1 ? 'Referral not exist' : null);

    if (errMsg) {
        return (
            <ErrorDisplay error={new Error(errMsg)} title="Error loading referral" />
        );
    }

    const parsedReferrals = data!.referrals
        .map((referral) => portalReferralParse(referral))
        .sort((a, b) => {
            return a.guid === id ? -1 : b.guid === id ? 1 : 0
        }); // sort prioritized root referral

    const referralByGuid = groupBy(parsedReferrals, p => p.guid);

    if (selectedReferral$.getValue() === null) {
        selectedReferral$.next(find(parsedReferrals, r => r.guid === parsedReferrals[0].guid) as Referral);
    } else {
        selectedReferral$.next(find(parsedReferrals, r => r.guid === selectedReferral$.getValue()!.guid) as Referral);
    }

    const offerByReferralGuid = groupBy(
        data!.offersByReferrals
            .map((offer) => ReferralOfferToOffer(offer, referralByGuid[offer.referralGuid][0])),
        p => p.referralGuid);

    return (
        <div className='h-screen placement-provider-offer-page-bg'>
            <ProviderOfferNavigationBar selectedReferral$={selectedReferral$}/>
            <div className='placement-provider-offer-page-height'>
                <ProviderOfferDetailLeftRail referrals={parsedReferrals}
                                             selectedReferral$={selectedReferral$}/>
                <ProviderOfferDetailRightRail offersByReferralGuid={offerByReferralGuid}
                                              referrals={parsedReferrals}
                                              selectedReferral$={selectedReferral$}
                                              recentPage={recentPage}
                                              refetchReferrals={() => {
                                                  refetch()
                                              }}
                />
            </div>
        </div>
    );
}
